import { useState, useEffect, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { useDarkMode } from "../../../../../context/ThemeContext";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useAlert } from "../../../../../context/AlertContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import {
  fetchNotificationById,
  updateNotification,
  useNotificationsStore,
  type Notification,
  type NotificationDto,
} from "../../../../../store/admin/notificationsStore";
import {
  useNotificationsList,
  type NotificationListResult,
} from "../../../../../features/notifications/queries";
import { removeLiteNotificationFromCache } from "../../../../../features/notifications/liteQueries";
import {
  NOTIFICATION_DEEP_LINK_MESSAGES,
  NOTIFICATION_PARAM,
  resolveNotificationDeepLink,
  type NotificationFetchResult,
} from "../../../../../features/notifications/notificationDeepLink";
import { isAbortError } from "../../../../../utils/api";
import { useRolesStore } from "../../../../../store/admin/rolesStore";
import { useResellerOrAdminUsers } from "../../../../../features/users/queries";
import { useIsReseller } from "../../../../../hooks/useIsReseller";
import { getThemeClasses, getCustomSelectStyles } from "../../../shared";
import { createNotification } from "../../../../../store/admin/notificationsStore";

// Components
import NotificationsHeader from "./NotificationsHeader";
import NotificationsTable from "./NotificationsTable";
import NotificationCard from "./NotificationCard";
import AddNotificationModal from "./modals/AddNotificationModal";
import NotificationDetailsModal from "./modals/NotificationDetailsModal";
import EditNotificationModal from "./modals/EditNotificationModal";
import { useUserStore } from "../../../../../store/userStore";
import PaginationControls from "../../../../../components/base/PaginationControls";

export default function NotificationsSection() {
  const { isDarkMode } = useDarkMode();
  const { t } = useTranslation("adminSections");
  const { showAlert } = useAlert();
  const lang = localStorage.getItem("lang") || "es";

  // Theme
  const themeClasses = getThemeClasses(isDarkMode);
  const customSelectStyles = getCustomSelectStyles(isDarkMode);

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [sendToAllMode, setSendToAllMode] = useState(false);

  // View
  const [selectedView, setSelectedView] = useState<"grid" | "list">("list");

  // Filters
  const [selectedType, setSelectedType] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Selected notification
  const [selectedNotification, setSelectedNotification] =
    useState<Notification | null>(null);

  // Loading & Image
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // First render ref
  const isFirstRender = useRef(true);

  const filterParams = useMemo(() => {
    const params: {
      page: number;
      limit: number;
      type_of_addressee?: string;
      status?: string;
      search?: string;
    } = { page: currentPage, limit: itemsPerPage };
    if (selectedType !== "all") params.type_of_addressee = selectedType;
    if (selectedStatus !== "all") params.status = selectedStatus;
    if (debouncedSearch) params.search = debouncedSearch;
    return params;
  }, [currentPage, itemsPerPage, selectedType, selectedStatus, debouncedSearch]);

  const notificationsQuery = useNotificationsList(filterParams);
  const notifications = notificationsQuery.data?.data ?? [];
  const loading = notificationsQuery.isLoading;
  const error = notificationsQuery.error ? "Error" : null;
  const meta = notificationsQuery.data?.meta;
  const markNotificationAsRead = useNotificationsStore((state) => state.markNotificationAsRead);
  const totalPages = meta?.totalPages || 1;

  const queryClient = useQueryClient();
  const invalidateNotifications = () =>
    queryClient.invalidateQueries({ queryKey: ["notifications"] });

  const handleViewNotification = (notification: Notification) => {
    handleSelectNotification(notification);
    if (!notification.readed) {
      queryClient.setQueriesData<NotificationListResult>(
        { queryKey: ["notifications", "list"] },
        (old) =>
          old
            ? {
                ...old,
                data: old.data.map((n) =>
                  n.id === notification.id ? { ...n, readed: true } : n,
                ),
              }
            : old,
      );
      removeLiteNotificationFromCache(notification.id);
      markNotificationAsRead(notification.id);
    }
    setShowDetailsModal(true);
  };

  const user = useUserStore((state) => state.user);
  const usersQuery = useResellerOrAdminUsers(user?.role_id);
  const users = usersQuery.data ?? [];
  
  // Roles
  const roles = useRolesStore((state) => state.roles);
  const fetchRoles = useRolesStore((state) => state.fetchRoles);

  // Check if user is reseller
  const { isReseller } = useIsReseller();

  // Form
  const form = useForm<NotificationDto>({
    mode: "onBlur",
    defaultValues: {
      lang: lang,
      title: "",
      description: "",
      type_of_addresse: "",
      addressee: "",
      priority: "medium",
      icon: "",
      status: "scheduled",
      image: "",
      schedule_shipment: "",
    },
  });

  const { setValue, reset } = form;

  // Effects
  useEffect(() => {
    fetchRoles();
  }, []);

  // ── Deep link `?notification=<id>` ───────────────────────────────────────
  // Llega desde el modal de Inspección de Descarga ("Ver notificación" en los
  // pasos de geocerca). Se trae por id en vez de buscarla en `notifications`:
  // esa lista es UNA página de 10 de todas las del usuario, y la de un cruce de
  // ayer ya no está en la primera. Qué se hace con cada respuesta lo decide
  // `resolveNotificationDeepLink` (puro, con su test); acá sólo va el cableado.
  //
  // Mismo esqueleto que `?event=` en accessSection/page.tsx, por las mismas dos
  // razones que están medidas ahí:
  //  · El ref se marca al TERMINAR, no antes del await. Marcado antes, el doble
  //    invoke de StrictMode cancela la primera corrida y la segunda se
  //    auto-bloquea: el modal no abre nunca.
  //  · Lo que cambia en cada render (el handler que abre el modal, `showAlert`,
  //    `t`) entra por ref y NO va en las dependencias. `handleViewNotification`
  //    es una función nueva en cada render; con ella en las deps el cleanup
  //    cancelaría el fetch en vuelo en cada re-render.
  //
  // El param se limpia en TODA salida, también en las de error: con el id
  // todavía en la URL el efecto volvería a pedir una notificación que va a
  // volver a fallar, y al cerrar el modal se reabriría sola.
  const [searchParams, setSearchParams] = useSearchParams();
  const userId = user?.id ?? null;
  const handledNotificationRef = useRef<string | null>(null);
  const deepLinkHandlersRef = useRef({
    open: handleViewNotification,
    showAlert,
    t,
  });
  deepLinkHandlersRef.current = { open: handleViewNotification, showAlert, t };

  useEffect(() => {
    const requestedId = searchParams.get(NOTIFICATION_PARAM)?.trim();
    if (!requestedId) {
      // Sin param el ref ya cumplió. Soltarlo deja que el MISMO id se pueda
      // volver a abrir sin desmontar la sección (otro clic al mismo enlace).
      handledNotificationRef.current = null;
      return;
    }
    if (handledNotificationRef.current === requestedId) return;
    // Sin usuario no se puede decidir si es destinatario, y decidirlo igual
    // contestaría "No tienes acceso" a quien sí lo tiene. Se espera: el efecto
    // vuelve a correr cuando el store hidrata (`userId` está en las deps).
    if (!userId) return;

    const controller = new AbortController();
    let cancelled = false;
    (async () => {
      let result: NotificationFetchResult<Notification>;
      try {
        result = {
          ok: true,
          notification: await fetchNotificationById(
            requestedId,
            controller.signal,
          ),
        };
      } catch (err) {
        // Un abort es este mismo efecto desmontándose (o el logout): no es una
        // respuesta, y tratarlo como fallo mostraría una alerta de error al
        // salir de la pantalla.
        if (cancelled || isAbortError(err)) return;
        result = {
          ok: false,
          status: (err as { response?: { status?: number } })?.response?.status,
        };
      }
      if (cancelled) return;

      handledNotificationRef.current = requestedId;
      const next = new URLSearchParams(searchParams);
      next.delete(NOTIFICATION_PARAM);
      setSearchParams(next, { replace: true });

      const action = resolveNotificationDeepLink(result, userId);
      const handlers = deepLinkHandlersRef.current;
      if (action.type === "open") {
        handlers.open(action.notification);
        return;
      }
      const message = NOTIFICATION_DEEP_LINK_MESSAGES[action.reason];
      handlers.showAlert({
        title: handlers.t(`${message.key}.title`, message.title),
        text: handlers.t(`${message.key}.text`, message.text),
        icon: action.reason === "failed" ? "error" : "warning",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        onConfirm: () => {},
      });
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [searchParams, setSearchParams, userId]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setCurrentPage(1);
  }, [selectedType, selectedStatus, debouncedSearch]);

  // Handlers
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearImage = () => {
    setImagePreview(null);
    setSelectedFile(null);
    const input = document.getElementById("image") as HTMLInputElement;
    if (input) input.value = "";
  };

  const handleSelectNotification = (notification: Notification) => {
    setSelectedNotification(notification);
    setValue("title", notification.title);
    setValue("description", notification.description);
    setValue("type_of_addresse", notification.type_of_addresse);
    setValue("addressee", notification.addressee);
    setValue("priority", notification.priority);
    setValue("icon", notification.icon);
    setValue("status", notification.status);

    if (notification.schedule_shipment) {
      let scheduleValue = "";
      let date: Date | null = null;

      if (
        typeof notification.schedule_shipment === "object" &&
        (notification.schedule_shipment as { _seconds: number })._seconds
      ) {
        // Es un timestamp de Firestore
        date = new Date(
          (notification.schedule_shipment as { _seconds: number })._seconds *
            1000,
        );
      } else if (typeof notification.schedule_shipment === "string") {
        // Ya es un string ISO (UTC), convertirlo
        date = new Date(notification.schedule_shipment);
      }

      if (date && !isNaN(date.getTime())) {
        // Convertir UTC a hora local para mostrar en el input datetime-local
        // El input datetime-local espera formato: YYYY-MM-DDTHH:mm en hora LOCAL
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        scheduleValue = `${year}-${month}-${day}T${hours}:${minutes}`;
      }

      setValue("schedule_shipment", scheduleValue);
    } else {
      setValue("schedule_shipment", "");
    }
  };

  const onSubmit = async (
    data: NotificationDto,
    file: File | null,
    removeImage?: boolean,
    selectedUsers?: string[],
  ) => {
    setLoadingSubmit(true);
    try {
      // Determine if sending to all (admin reseller with empty array OR all company users selected)
      const isSendToAll = (isReseller && selectedUsers && selectedUsers.length === 0) || 
                          (selectedUsers && selectedUsers.length === users.length);

      const formData = new FormData();
      formData.append("lang", data.lang);
      formData.append("title", data.title);
      formData.append("description", data.description);
      formData.append("type_of_addresse", isSendToAll ? "all" : "specific");
      formData.append("priority", data.priority);
      formData.append("icon_id", data.icon);
      formData.append("company_id", isSendToAll && isReseller ? "3bBceDQdrzCgi5kcV6gS" : (user?.company_id || ""));
      formData.append("createdBy", user?.id || "");

      // Send addresses array (user IDs) - only if not sending to all system users
      if (selectedUsers && selectedUsers.length > 0) {
        selectedUsers.forEach((userId, index) => {
          formData.append(`addresses[${index}]`, userId);
        });
      }

      if (!selectedNotification) {
        formData.append(
          "status_id",
          data.schedule_shipment ? "scheduled" : "sent",
        );
      }

      if (data.schedule_shipment) {
        // Convertir la fecha local a ISO UTC para consistencia entre zonas horarias
        const localDate = new Date(data.schedule_shipment);
        const isoUtcDate = localDate.toISOString();
        formData.append("schedule_shipment", isoUtcDate);
      }

      if (file) {
        formData.append("imageFile", file);
      } else if (removeImage) {
        formData.append("image", "");
      }

      if (selectedNotification) {
        await updateNotification(
          selectedNotification?.id || "",
          formData,
          showAlert,
          setLoadingSubmit,
          t,
          () => {
            setShowEditModal(false);
            setSelectedNotification(null);
          },
          reset,
        );
      } else {
        await createNotification(
          formData,
          showAlert,
          setLoadingSubmit,
          t,
          () => {
            setShowAddModal(false);
            clearImage();
          },
          reset,
        );
      }
      setSelectedNotification(null);
      invalidateNotifications();
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setSendToAllMode(false);
    reset();
    clearImage();
  };

  return (
    <div className="h-full flex flex-col">
      <NotificationsHeader
        isDarkMode={isDarkMode}
        themeClasses={themeClasses}
        customSelectStyles={customSelectStyles}
        selectedType={selectedType}
        setSelectedType={setSelectedType}
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
        search={search}
        setSearch={setSearch}
        onAddClick={() => {
          setSendToAllMode(false);
          setShowAddModal(true);
        }}
        onSendToAllClick={() => {
          setSendToAllMode(true);
          setShowAddModal(true);
        }}
        isReseller={isReseller}
        selectedView={selectedView}
        setSelectedView={setSelectedView}
      />

      <div className="flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-6">
        {loading ? (
          <div className="text-center text-gray-500">
            {t("notifications.loading")}
          </div>
        ) : error ? (
          <div className="text-center text-red-500">{error}</div>
        ) : selectedView === "grid" ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4 sm:gap-6">
              {notifications.length > 0 ? (
                notifications.map((notification) => (
                  <NotificationCard
                    key={notification.id}
                    notification={notification}
                    isDarkMode={isDarkMode}
                    themeClasses={themeClasses}
                    onView={() => handleViewNotification(notification)}
                    onEdit={() => {
                      handleSelectNotification(notification);
                      setShowEditModal(true);
                    }}
                  />
                ))
              ) : (
                <div className="flex flex-col items-center justify-center w-full col-span-full py-16">
                  <i
                    className={`ri-notification-off-line text-5xl ${themeClasses.textSecondary} mb-4`}
                  ></i>
                  <h3
                    className={`text-lg font-medium ${themeClasses.text} mb-2`}
                  >
                    {t("notifications.noNotifications")}
                  </h3>
                  <p className={`${themeClasses.textSecondary} text-sm`}>
                    {t("notifications.noNotificationsDescription")}
                  </p>
                </div>
              )}
            </div>
            {/* Pagination Grid */}
            {notifications.length > 0 && totalPages > 1 && (
              <div className="mt-6">
                <PaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </>
        ) : (
          <>
            <NotificationsTable
              themeClasses={themeClasses}
              notifications={notifications}
              loading={loading}
              error={error}
              onViewNotification={(notification) => handleViewNotification(notification)}
              onEditNotification={(notification) => {
                handleSelectNotification(notification);
                setShowEditModal(true);
              }}
            />

            {/* Pagination List */}
            {notifications.length > 0 && totalPages > 1 && (
              <div className="py-6">
                <PaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      {showAddModal && (
        <AddNotificationModal
          isDarkMode={isDarkMode}
          themeClasses={themeClasses}
          customSelectStyles={customSelectStyles}
          form={form}
          users={users}
          roles={roles}
          imagePreview={imagePreview}
          selectedFile={selectedFile}
          loadingSubmit={loadingSubmit}
          isReseller={isReseller}
          sendToAllMode={sendToAllMode}
          onClose={handleCloseAddModal}
          onSubmit={onSubmit}
          onImageChange={handleImageChange}
          onClearImage={clearImage}
        />
      )}

      {showDetailsModal && selectedNotification && (
        <NotificationDetailsModal
          isDarkMode={isDarkMode}
          themeClasses={themeClasses}
          notification={selectedNotification}
          users={users}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedNotification(null);
            reset();
          }}
          onEdit={() => {
            setShowDetailsModal(false);
            setShowEditModal(true);
          }}
        />
      )}

      {showEditModal && selectedNotification && (
        <EditNotificationModal
          isDarkMode={isDarkMode}
          themeClasses={themeClasses}
          customSelectStyles={customSelectStyles}
          form={form}
          users={users}
          roles={roles}
          notification={selectedNotification}
          loadingSubmit={loadingSubmit}
          onClose={() => {
            setShowEditModal(false);
            setSelectedNotification(null);
            reset();
          }}
          onSubmit={onSubmit}
        />
      )}

    </div>
  );
}
